const API_BASE = '/api/v1';

/**
 * Get stored API key (from localStorage or empty for unauthenticated mode).
 */
function getApiKey() {
  return localStorage.getItem('ocr_api_key') || '';
}

/**
 * Set API key in localStorage.
 */
export function setApiKey(key) {
  localStorage.setItem('ocr_api_key', key);
}

/**
 * Build headers with optional API key.
 */
function authHeaders(extra = {}) {
  const headers = { ...extra };
  const key = getApiKey();
  if (key) headers['X-API-Key'] = key;
  return headers;
}

async function handleResponse(res) {
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText }));
    const error = new Error(err.error || err.message || `Request failed: ${res.status}`);
    error.statusCode = res.status;
    error.code = err.code;
    throw error;
  }
  return res;
}

export function uploadFile(file, options = {}, onProgress) {
  const params = new URLSearchParams();
  if (options.format) params.set('format', options.format);
  if (options.legal) params.set('legal', 'true');
  if (options.lang) params.set('lang', options.lang);

  const query = params.toString();
  const url = `${API_BASE}/upload${query ? `?${query}` : ''}`;

  const formData = new FormData();
  formData.append('file', file);

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    const headers = authHeaders();
    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    if (onProgress) {
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          onProgress(Math.round((e.loaded / e.total) * 100));
        }
      });
    }

    xhr.addEventListener('load', () => {
      try {
        const data = JSON.parse(xhr.responseText);
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(data);
        } else {
          const error = new Error(data.error || data.message || `Request failed: ${xhr.status}`);
          error.statusCode = xhr.status;
          error.code = data.code;
          reject(error);
        }
      } catch {
        const error = new Error(`Request failed: ${xhr.status}`);
        error.statusCode = xhr.status;
        reject(error);
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.send(formData);
  });
}

export async function getTaskStatus(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}`, {
    headers: authHeaders(),
  });
  return (await handleResponse(res)).json();
}

export async function getTaskRaw(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/raw`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Not ready: ${res.status}`);
  return res.text();
}

export async function getTaskRender(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}/render`, {
    headers: authHeaders(),
  });
  if (!res.ok) throw new Error(`Not ready: ${res.status}`);
  return res.text();
}

export async function deleteTask(taskId) {
  const res = await fetch(`${API_BASE}/tasks/${taskId}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  return (await handleResponse(res)).json();
}

export async function getHealth() {
  const res = await fetch(`${API_BASE}/health`);
  return res.json();
}

export function getDownloadUrl(taskId) {
  return `${API_BASE}/tasks/${taskId}/download`;
}

export function hasApiKey() {
  return !!getApiKey();
}
